import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { DateRange } from "react-date-range";
import { format } from "date-fns";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import "./PendingPayouts.css";
import { downloadPendingPayoutTxns, pendingPayoutTnx } from "../../../config/LiveApi";

function PendingPayouts() {
  /* ---------------- STATE ---------------- */

  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const pickerRef = useRef(null);

  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection"
    }
  ]);

  /* ---------------- API ---------------- */


  /* ---------------- DATE CONVERT ---------------- */

  const getUtcRange = () => {

    const start = new Date(dateRange[0].startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(dateRange[0].endDate);
    end.setHours(23, 59, 59, 999);

    return {
      fromDate: start.toISOString(),
      toDate: end.toISOString()
    };
  };


  /* ---------------- FETCH SUMMARY ---------------- */

  const fetchSummary = async () => {

    try {

      setLoading(true);

      const { fromDate, toDate } = getUtcRange();

      const res = await pendingPayoutTnx(fromDate, toDate);

      setSummary(res.data || []);

    } catch (err) {

      console.error(err);
      alert("Failed to fetch pending payouts");

    } finally {

      setLoading(false);

    }

  };


  /* ---------------- DOWNLOAD SINGLE ---------------- */

 const downloadSingle = async (row) => {

  try {

    setDownloading(true);

    const { fromDate, toDate } = getUtcRange();

    const res = await downloadPendingPayoutTxns({
      fromDate,
      toDate,
      orgName: row.orgName,
      pg: row.pg
    });

    const data = res.data;

    if (!data?.length) {

      alert("No pending payouts found");
      return;

    }

    const excelData = data.map((tx) => ({
      
      ReferenceID: tx.referenceId,
      Organisation: tx.orgName,
      Provider: tx.pg,
      Amount: tx.requestAmount,
      UTR: tx.responseUTR || "N/A"

    }));


    const ws = XLSX.utils.json_to_sheet(excelData);

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      `${row.orgName}_${row.pg}`
    );

    XLSX.writeFile(
      wb,
      `${row.orgName}_${row.pg}_PendingPayouts.xlsx`
    );

  } catch (err) {

    console.error(err);
    alert("Download failed");

  } finally {

    setDownloading(false);

  }

};


  /* ---------------- DOWNLOAD ALL ---------------- */

  const downloadAll = async () => {
    if (!summary.length) return alert("No data available");

    for (const row of summary) {

      await downloadSingle(row);

    }

  };


  /* ---------------- CLOSE CALENDAR ---------------- */

  useEffect(() => {

    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);



  /* ---------------- UI ---------------- */

  return (
    <div className="pending-container">
      <h2 className="title">💰 Pending Payout Summary</h2>

      {/* FILTER */}

      <div className="filter-card">
        <div className="date-section">
          <label>Select Date Range</label>

          <div
            className="date-input"
            onClick={() => setShowPicker(!showPicker)}
          >
            {format(dateRange[0].startDate, "dd MMM yyyy")}
            {" — "}
            {format(dateRange[0].endDate, "dd MMM yyyy")}
          </div>

          {showPicker && (
            <div className="calendar" ref={pickerRef}>
              <DateRange
                ranges={dateRange}
                onChange={(item) => setDateRange([item.selection])}
                moveRangeOnFirstSelection={false}
                rangeColors={["#fc8019"]}
              />
            </div>
          )}
        </div>

        <div className="button-group">
          <button
            className="btn primary"
            onClick={fetchSummary}
            disabled={loading}
          >
            {loading ? "Fetching..." : "Fetch Data"}
          </button>

          <button
            className="btn download"
            onClick={downloadAll}
            disabled={downloading}
          >
            ⬇ Download All
          </button>
        </div>
      </div>

      {/* SUMMARY GRID */}

      <div className="grid">
        {summary.map((row, index) => (
          <div
            key={index}
            className="summary-card"
            onClick={() => downloadSingle(row)}
          >
            <div className="card-header">
              <h3>{row.orgName}</h3>

              <span className="pg">{row.pg}</span>
            </div>

            <div className="card-body">
              <p>
                Total Pending:
                <strong> {row.total}</strong>
              </p>

              <p>
                Processing:
                <strong> {row.processing}</strong>
              </p>

              <p className="click-text">⬇ Click to download</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PendingPayouts;